import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

const ReviewerRegister = () => {
  const [formData, setFormData] = useState({
    UserName: '',
    Password: '',
    ReviewerName: '',
    Address: '',
    EmailID: '',
    ContactNumber: '',
    AgreementFile: null,
    Profile: null,
    IsAgree: false,
    PlatformID: '',
  });

  const [showTerms, setShowTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');

  const navigate = useNavigate();

  const API = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const platformID = localStorage.getItem('platformID');
    setFormData(prev => ({ ...prev, PlatformID: platformID || '' }));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'UserName') {
      // Remove spaces from username as user types
      const noSpaces = value.replace(/\s/g, '');
      setFormData(prev => ({ ...prev, [name]: noSpaces }));
      return;
    }

    if (name === 'ContactNumber') {
      // Allow only digits in ContactNumber
      const numbersOnly = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: numbersOnly }));
      return;
    }

    if (name === 'EmailID') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailError(emailRegex.test(value) ? '' : 'Please enter a valid email address.');
    }

    if (name === 'Password') {
      evaluatePasswordStrength(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files.length === 0) return;

    const file = files[0];
    // Validate PDF file only
    if (file.type !== 'application/pdf') {
      alert('Only PDF files are allowed.');
      e.target.value = null; // Reset file input
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: file,
    }));
  };

  const evaluatePasswordStrength = (password) => {
    if (password.length < 6) setPasswordStrength('Weak');
    else if (/[A-Z]/.test(password) && /\d/.test(password)) setPasswordStrength('Strong');
    else setPasswordStrength('Medium');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.IsAgree) {
      alert('You must agree to the Terms & Conditions');
      return;
    }

    if (emailError) {
      alert('Please correct the email before submitting.');
      return;
    }

    setIsSubmitting(true);

    const formDataToSend = new FormData();
    for (const key in formData) {
      // Only append non-null values and files properly
      if (formData[key] !== null && formData[key] !== '') {
        formDataToSend.append(key, formData[key]);
      }
    }

    try {
      const response = await axios.post(
        `${API}/api/reviewer/register`,
        formDataToSend,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      alert(response.data.message || 'Registration successful!');
      setIsRegistered(true);

      // Reset form
      setFormData({
        UserName: '',
        Password: '',
        ReviewerName: '',
        Address: '',
        EmailID: '',
        ContactNumber: '',
        AgreementFile: null,
        Profile: null,
        IsAgree: false,
        PlatformID: localStorage.getItem('platformID') || '',
      });

      setEmailError('');
      setPasswordStrength('');
    } catch (error) {
      console.error('Registration error:', error);
      alert(error.response?.data?.message || 'An error occurred during registration.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="container mt-5 mb-5 d-flex justify-content-center">
        <div className="card p-4 shadow-sm w-100" style={{ maxWidth: '700px' }}>
          {!isSubmitting && !isRegistered && (
            <>
              <h3 className="text-center mb-4">Reviewer Registration</h3>

              <form onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="row">
                  <div className="form-group mb-3 col-md-6">
                    <label className="form-label fw-semibold">User Name (no spaces)</label>
                    <input
                      type="text"
                      name="UserName"
                      className="form-control"
                      value={formData.UserName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group mb-3 col-md-6">
                    <label className="form-label fw-semibold">Password</label>
                    <input
                      type="password"
                      name="Password"
                      className="form-control"
                      value={formData.Password}
                      onChange={handleChange}
                      required
                    />
                    {passwordStrength && (
                      <small className={`form-text text-${passwordStrength === 'Strong' ? 'success' : passwordStrength === 'Medium' ? 'warning' : 'danger'}`}>
                        Password Strength: {passwordStrength}
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Reviewer Name</label>
                  <input
                    type="text"
                    name="ReviewerName"
                    className="form-control"
                    value={formData.ReviewerName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Address</label>
                  <textarea
                    name="Address"
                    className="form-control"
                    rows={3}
                    value={formData.Address}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Email</label>
                  <input
                    type="email"
                    name="EmailID"
                    className={`form-control ${emailError ? 'is-invalid' : ''}`}
                    value={formData.EmailID}
                    onChange={handleChange}
                    required
                  />
                  {emailError && <div className="invalid-feedback">{emailError}</div>}
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Contact Number (digits only)</label>
                  <input
                    type="tel"
                    name="ContactNumber"
                    className="form-control"
                    maxLength={10}
                    pattern="\d{10}"
                    title="Enter a 10-digit number"
                    value={formData.ContactNumber}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Identification (Aadhar / PAN) (PDF only)</label>
                  <input
                    type="file"
                    name="AgreementFile"
                    className="form-control"
                    onChange={handleFileChange}
                    accept=".pdf"
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label fw-semibold">Profile (PDF only)</label>
                  <input
                    type="file"
                    name="Profile"
                    className="form-control"
                    onChange={handleFileChange}
                    accept=".pdf"
                    required
                  />
                </div>

                <div className="form-group form-check mb-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="IsAgree"
                    name="IsAgree"
                    checked={formData.IsAgree}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="IsAgree">
                    I agree to the{' '}
                    <span
                      style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                      onClick={() => setShowTerms(true)}
                    >
                      Terms & Conditions
                    </span>
                  </label>
                </div>

                <button type="submit" className="btn btn-primary w-100" disabled={!!emailError || isSubmitting}>
                  Register
                </button>
              </form>
            </>
          )}

          {isSubmitting && (
            <div className="text-center py-5">
              <h5>Please wait while we process your registration...</h5>
            </div>
          )}

          {isRegistered && (
            <div className="text-left py-5">
              <h5 className="fw-bold text-success mb-4">Registration submitted successfully!</h5>
              <p className="mb-2"><strong>A.</strong> You can use your login credentials to visit the platform dashboard only after host validation.</p>
              <p className="mb-2"><strong>B.</strong> You shall receive an intimation email.</p>
              <p className="mb-4"><strong>C.</strong> Incomplete registrations will remain pending until all details are provided.</p>

              <div className="text-center">
                <button
                  className="btn btn-success px-4"
                  onClick={() => navigate('/')}
                >
                  OK
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Terms & Conditions Modal */}
      <Modal show={showTerms} onHide={() => setShowTerms(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Reviewer Terms & Conditions</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <p>1. The information provided on this website is for general informational purposes only...</p>
          <p>2. This website does not provide legal, financial, medical, or professional advice...</p>
          <p>3. We are not liable for any losses or damages arising from the use of this website...</p>
          <p>4. This website is platform-based; submission, review, and selection are handled by hosts...</p>
          <p>5. Hosts, authors, and reviewers are responsible for the content they submit or approve...</p>
          <p>6. This site facilitates article/paper submission workflows, but does not guarantee acceptance...</p>
          <p>7. All content, workflow, and intellectual property belongs to the platform provider...</p>
          <p>8. We may collect personal data as outlined in our Privacy Policy...</p>
          <p>9. Endorsements and testimonials reflect personal experiences and may not be typical...</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTerms(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ReviewerRegister;
